import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Eye, Download, Mail, Phone, Linkedin, Star, Loader2, LinkIcon, StickyNote } from "lucide-react";
import { UpvMark } from "@/components/brand/Brand";
import { LangSwitcher } from "@/components/i18n/LangSwitcher";
import { api, apiError, assetUrl } from "@/lib/api";
import { fullName, initials, DAY_LONG } from "@/lib/demo/utils";
import { shortTit } from "@/lib/demo/seed";
import { ORIGIN_SHORT } from "@/lib/i18n/dictInteracciones";
import { useI18n } from "@/lib/i18n/i18n";
import { Input } from "@/components/ui/input";
import { ReviewControls, useReviewer } from "@/components/portal/ColleagueFeedback";

const Contact = ({ icon: Icon, value }) => (value ? <span className="flex min-w-0 items-center gap-1.5"><Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" /><span className="truncate">{value}</span></span> : null);

const Notes = ({ notes }) => {
  const { t, lang } = useI18n();
  if (!notes?.length) return null;
  return (
    <div className="mt-4 space-y-2 border-t border-slate-100 pt-4">
      <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400"><StickyNote className="h-3.5 w-3.5" /> {t("Notas del equipo")}</p>
      {notes.map((n) => <p key={n.id} className="rounded-xl bg-slate-50 px-3 py-2 text-sm"><span className="whitespace-pre-line">{n.text}</span><span className="mt-1 block text-[11px] text-slate-400">{n.userName} · {new Date(n.createdAt).toLocaleDateString(lang)}</span></p>)}
    </div>
  );
};

const FavCard = ({ row, token, i, reviewer }) => {
  const { t } = useI18n();
  const { participant: p, interaction: it } = row;
  const cv = `/api/shared/${token}/cv/${p.id}`;
  return (
    <article className="fade-up rounded-[24px] border border-slate-200 bg-white p-6" style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }} data-testid={`shared-card-${p.id}`}>
      <div className="flex items-start gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-upv font-display font-bold text-white">{initials(p)}</span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-xl font-bold" data-testid={`shared-name-${p.id}`}>{fullName(p)}</p>
          <p className="text-sm text-slate-600">{t(shortTit(p.titulacion))} · {p.centro} · {t(p.curso)}</p>
        </div>
        <Star className="h-5 w-5 shrink-0 fill-amber-400 text-amber-500" />
      </div>
      <div className="mt-4 grid gap-2 text-sm text-slate-700 sm:grid-cols-3">
        <Contact icon={Mail} value={p.email} /><Contact icon={Phone} value={p.phone} /><Contact icon={Linkedin} value={p.linkedin} />
      </div>
      <p className="mt-3 text-xs text-slate-500">{t(ORIGIN_SHORT[it.origin] || "")} · {DAY_LONG[it.day] ? t(DAY_LONG[it.day]) : it.date} · {it.time}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <a href={assetUrl(cv)} target="_blank" rel="noopener noreferrer" className="flex h-10 items-center gap-2 rounded-xl bg-upv px-4 text-xs font-bold text-white hover:bg-upv-dark" data-testid={`shared-view-cv-${p.id}`}><Eye className="h-3.5 w-3.5" /> {t("VER CV")}</a>
        <a href={assetUrl(`${cv}?download=true`)} className="flex h-10 items-center gap-2 rounded-xl border border-slate-200 px-4 text-xs font-bold hover:border-slate-900" data-testid={`shared-download-cv-${p.id}`}><Download className="h-3.5 w-3.5" /> {t("DESCARGAR CV")}</a>
      </div>
      <Notes notes={row.notes} />
      <ReviewControls token={token} pid={p.id} reviewer={reviewer} initial={row.myFeedback} />
    </article>
  );
};

export default function Favoritos() {
  const { token } = useParams();
  const { t, lang } = useI18n();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [reviewer, setName] = useReviewer();
  useEffect(() => {
    const metas = [["robots", "noindex, nofollow"], ["referrer", "no-referrer"]].map(([name, content]) => {
      const m = document.createElement("meta"); m.name = name; m.content = content; document.head.appendChild(m); return m;
    });
    return () => metas.forEach((m) => m.remove());
  }, []);
  useEffect(() => { api.get(`/shared/${token}`, { params: { reviewer: reviewer.id } }).then((r) => setD(r.data)).catch((e) => setErr(apiError(e).message)); }, [token, reviewer.id]);

  return (
    <div className="min-h-screen bg-[#F6F7F9]" data-testid="shared-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-5"><UpvMark /><LangSwitcher testPrefix="shared-lang" /></div>
      </header>
      <main className="mx-auto max-w-5xl px-5 py-10">
        {!d && !err && <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" />}
        {err && (
          <div className="mx-auto max-w-md rounded-[28px] bg-white p-10 text-center" data-testid="shared-error">
            <LinkIcon className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-4 font-display text-xl font-bold">{t(err)}</p>
            <p className="mt-2 text-sm text-slate-500">{t("Pide a la empresa un enlace nuevo.")}</p>
          </div>
        )}
        {d && (
          <>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Favoritos compartidos")} · {d.company.name}</p>
            <h1 className="mt-2 font-display text-4xl font-bold tracking-tight sm:text-5xl" data-testid="shared-title">{d.label || t("Candidatos favoritos")}</h1>
            <p className="mt-3 text-sm text-slate-500" data-testid="shared-meta">{t("Compartido por {name} · válido hasta el {d}", { name: d.createdByName, d: new Date(d.expiresAt).toLocaleDateString(lang, { dateStyle: "long" }) })}</p>
            <p className="mt-1 text-xs text-slate-400">{t("Información confidencial de candidatos. No la reenvíes fuera de tu organización.")}</p>
            {d.items.length > 0 && (
              <label className="mt-6 flex max-w-md flex-col gap-1 text-xs font-semibold text-slate-500">
                {t("Tu nombre (para que el equipo sepa quién opina)")}
                <Input value={reviewer.name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder={t("Ej.: Laura, Dirección técnica")} className="h-11 rounded-xl bg-white text-sm" data-testid="shared-reviewer-name" />
              </label>
            )}
            <div className="mt-6 grid gap-4 md:grid-cols-2">{d.items.map((r, i) => <FavCard key={r.participant.id} row={r} token={token} i={i} reviewer={reviewer} />)}</div>
            {d.items.length === 0 && <p className="mt-8 text-sm text-slate-500" data-testid="shared-empty">{t("La empresa aún no ha marcado candidatos favoritos.")}</p>}
          </>
        )}
      </main>
    </div>
  );
}
